import React, { useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useFormik } from "formik";
import * as Yup from "yup";
import { hideModal } from "./modalHelper";
import { API, STATUTS_TACHE, PRIORITES, findStatutTache, findPriorite, toDateInput, apiMessage, toast } from "./projetsCommon";

export const TACHE_MODAL_ID = "tacheFormModal";

const empty = {
    titre: "", description: "", phaseId: "", assigneId: "", dateDebut: "", echeance: "",
    statut: 1, priorite: 2, avancement: 0, chargeEstimeeHeures: "", chargeReelleHeures: "",
};

const hours = (label) => Yup.number().typeError(`${label} invalide`).nullable().transform((v, o) => (o === "" ? null : v)).min(0, `${label} invalide`);

const schema = Yup.object({
    titre: Yup.string().trim().required("Le titre est obligatoire").max(200, "200 caractères maximum"),
    echeance: Yup.string().nullable().test("apres", "L'échéance doit suivre le début", function (v) {
        const { dateDebut } = this.parent;
        return !v || !dateDebut || v >= dateDebut;
    }),
    chargeEstimeeHeures: hours("Heures"),
    chargeReelleHeures: hours("Heures"),
});

/**
 * Création / modification d'une tâche.
 * projetId : projet de la tâche ; tache = null → création ; defaultPhaseId : phase présélectionnée.
 * openKey : à incrémenter à chaque ouverture.
 */
const TacheFormModal = ({ projetId, tache, defaultPhaseId, openKey, phases, employes, onSaved }) => {
    const formik = useFormik({
        initialValues: empty,
        validationSchema: schema,
        onSubmit: async (v, { setErrors }) => {
            const num = (x) => (x === "" || x === null || x === undefined ? null : Number(x));
            const payload = {
                titre: v.titre.trim(),
                description: v.description?.trim() || null,
                phaseId: num(v.phaseId),
                assigneId: num(v.assigneId),
                dateDebut: v.dateDebut || null,
                echeance: v.echeance || null,
                statut: Number(v.statut),
                priorite: Number(v.priorite),
                avancement: Number(v.avancement) || 0,
                chargeEstimeeHeures: num(v.chargeEstimeeHeures),
                chargeReelleHeures: num(v.chargeReelleHeures),
            };
            try {
                if (tache) await axiosInstance.put(API.tache(projetId, tache.id), payload);
                else await axiosInstance.post(API.taches(projetId), payload);
                hideModal(TACHE_MODAL_ID);
                toast("success", tache ? "Tâche modifiée" : "Tâche créée");
                onSaved && onSaved();
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    useEffect(() => {
        formik.resetForm({
            values: tache ? {
                titre: tache.titre ?? "", description: tache.description ?? "",
                phaseId: tache.phaseId ?? "", assigneId: tache.assigneId ?? "",
                dateDebut: toDateInput(tache.dateDebut), echeance: toDateInput(tache.echeance),
                statut: findStatutTache(tache.statut).value, priorite: findPriorite(tache.priorite).value,
                avancement: tache.avancement ?? 0,
                chargeEstimeeHeures: tache.chargeEstimeeHeures ?? "", chargeReelleHeures: tache.chargeReelleHeures ?? "",
            } : { ...empty, phaseId: defaultPhaseId ?? "" },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tache, defaultPhaseId, openKey]);

    const f = formik;
    const invalid = (n) => (f.touched[n] && f.errors[n] ? "is-invalid" : "");
    const input = (name, label, props = {}) => (
        <>
            <label>{label}</label>
            <input name={name} className={`form-control ${invalid(name)}`} value={f.values[name] ?? ""}
                onChange={f.handleChange} onBlur={f.handleBlur} {...props} />
            <div className="invalid-feedback">{f.errors[name]}</div>
        </>
    );
    const statut = Number(f.values.statut);

    return (
        <div className="modal fade" id={TACHE_MODAL_ID}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5><i className="fas fa-tasks mr-2"></i>{tache ? "Modifier la tâche" : "Nouvelle tâche"}</h5>
                        <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                    </div>
                    <div className="modal-body">
                        <div className="form-group">{input("titre", "Titre *", { placeholder: "ex : Coulage dalle RDC" })}</div>
                        <div className="form-row">
                            <div className="form-group col-md-6">
                                <label>Phase</label>
                                <select name="phaseId" className="form-control" value={f.values.phaseId} onChange={f.handleChange}>
                                    <option value="">— Sans phase —</option>
                                    {phases.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                                </select>
                            </div>
                            <div className="form-group col-md-6">
                                <label>Assignée à</label>
                                <select name="assigneId" className="form-control" value={f.values.assigneId} onChange={f.handleChange}>
                                    <option value="">— Personne —</option>
                                    {employes.map((e) => <option key={e.id} value={e.id}>{e.nomComplet}{e.poste ? ` — ${e.poste}` : ""}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group col-md-3">{input("dateDebut", "Début", { type: "date" })}</div>
                            <div className="form-group col-md-3">{input("echeance", "Échéance", { type: "date" })}</div>
                            <div className="form-group col-md-3">
                                <label>Statut</label>
                                <select name="statut" className="form-control" value={f.values.statut} onChange={f.handleChange}>
                                    {STATUTS_TACHE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                            </div>
                            <div className="form-group col-md-3">
                                <label>Priorité</label>
                                <select name="priorite" className="form-control" value={f.values.priorite} onChange={f.handleChange}>
                                    {PRIORITES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="form-row align-items-end">
                            <div className="form-group col-md-3">{input("chargeEstimeeHeures", "Heures estimées", { type: "number", step: "0.5", min: 0 })}</div>
                            <div className="form-group col-md-3">{input("chargeReelleHeures", "Heures réelles", { type: "number", step: "0.5", min: 0 })}</div>
                            <div className="form-group col-md-6">
                                <label>Avancement : <strong>{statut === 4 ? 100 : f.values.avancement || 0} %</strong></label>
                                <input type="range" name="avancement" className="custom-range" min="0" max="100" step="10"
                                    value={statut === 4 ? 100 : f.values.avancement || 0} disabled={statut === 4} onChange={f.handleChange} />
                            </div>
                        </div>
                        <div className="form-group mb-0">
                            <label>Description</label>
                            <textarea name="description" rows={3} className="form-control" value={f.values.description} onChange={f.handleChange} />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                        <button type="button" className="btn btn-enregistrer" onClick={f.handleSubmit} disabled={f.isSubmitting}>Sauvegarder</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TacheFormModal;
